/**
 * GoogleAuthScreen
 * Handles Google OAuth callback from backend.
 *
 * Flow:
 * 1. Backend validates Google OAuth and redirects here with access_token in URL params
 * 2. This screen reads the params, stores auth data, and navigates to destination
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useRoute, useNavigation, CommonActions } from '@react-navigation/native';
import { useAuthStore } from '../store/authStore';
import type { AuthResponse } from '../api/types';
import { colors } from '../theme/colors';
import logger from '../utils/logger';

export default function GoogleAuthScreen() {
  const route = useRoute();
  const navigation = useNavigation();
  const { setAuth } = useAuthStore();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = (route.params || {}) as Record<string, string>;
    logger.log('[GoogleAuthScreen] Received params:', Object.keys(params));

    const accessToken = params.access_token;
    if (!accessToken) {
      logger.error('[GoogleAuthScreen] No access_token in params, redirecting to login');
      navigation.dispatch(
        CommonActions.reset({ index: 0, routes: [{ name: 'Auth' }] })
      );
      return;
    }

    const authData: AuthResponse = {
      token: accessToken,
      refresh_token: params.refresh_token,
      user_id: parseInt(params.user_id, 10) || 0,
      username: params.username || '',
      email: params.email || '',
      first_name: params.first_name,
      last_name: params.last_name,
    };

    const isNewUser = params.is_new_user === 'true';
    logger.log('[GoogleAuthScreen] Setting auth for user:', authData.email, 'isNewUser:', isNewUser);

    setAuth(authData).then(() => {
      logger.log('[GoogleAuthScreen] Auth set, navigating to Main');
      // Reset to Main stack — AppNavigator will handle showing the right screen
      navigation.dispatch(
        CommonActions.reset({ index: 0, routes: [{ name: 'Main' }] })
      );
    }).catch((error) => {
      logger.error('[GoogleAuthScreen] Failed to set auth:', error);
      navigation.dispatch(
        CommonActions.reset({ index: 0, routes: [{ name: 'Auth' }] })
      );
    });
  }, [route.params, setAuth, navigation]);

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" color={colors.primary} />
      <Text style={styles.text}>Connexion avec Google...</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  text: {
    marginTop: 16,
    fontSize: 16,
    color: colors.textSecondary,
  },
});
